import { useState, useEffect, useCallback } from 'react'
import {
  getVapidPublicKey,
  subscribeToPush,
  unsubscribeFromPush,
  getNotificationsEnabled,
  setNotificationsEnabled,
} from '@/services/notificationService'

type PermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported'

interface UsePushNotificationsResult {
  enabled: boolean
  loading: boolean
  permissionState: PermissionState
  toggle: () => Promise<void>
}

export function usePushNotifications(): UsePushNotificationsResult {
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [permissionState, setPermissionState] = useState<PermissionState>('prompt')

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPermissionState('unsupported')
      setLoading(false)
      return
    }

    const perm = Notification.permission
    setPermissionState(perm === 'default' ? 'prompt' : perm as PermissionState)

    getNotificationsEnabled()
      .then(setEnabled)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const toggle = useCallback(async () => {
    if (permissionState === 'unsupported') return

    setLoading(true)
    try {
      if (enabled) {
        // Disable notifications
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await unsubscribeFromPush(subscription.endpoint)
          await subscription.unsubscribe()
        }
        await setNotificationsEnabled(false)
        setEnabled(false)
      } else {
        // Enable notifications - request permission first
        const permission = await Notification.requestPermission()
        setPermissionState(permission as PermissionState)

        if (permission !== 'granted') {
          return
        }

        const vapidPublicKey = await getVapidPublicKey()
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        })

        await subscribeToPush(subscription)
        await setNotificationsEnabled(true)
        setEnabled(true)
      }
    } finally {
      setLoading(false)
    }
  }, [enabled, permissionState])

  return { enabled, loading, permissionState, toggle }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length) as Uint8Array<ArrayBuffer>
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
